import { useEffect, useState, useMemo } from "react";
import api, { brl } from "../lib/api";
import { toast } from "sonner";
import { CreditCard, ExternalLink, AlertTriangle, CheckCircle, Search, Plus, Minus, Trash2, X, Zap } from "lucide-react";

export default function InfinitePay() {
  const [pdvPending, setPdvPending] = useState([]);
  const [external, setExternal] = useState([]);
  const [sales, setSales] = useState([]);
  const [products, setProducts] = useState([]);
  const [open, setOpen] = useState(null); // external sale being confirmed
  const [manualOpen, setManualOpen] = useState(false); // quick-register modal
  const [cart, setCart] = useState([]);
  const [amountReceived, setAmountReceived] = useState(0);
  const [manualNotes, setManualNotes] = useState("");
  const [q, setQ] = useState("");

  const load = () => {
    api.get("/infinitepay/pending").then((r) => {
      setPdvPending(r.data.pdv_pending || []);
      setExternal(r.data.external || []);
    });
    api.get("/infinitepay/sales").then((r) => setSales(r.data));
    api.get("/products").then((r) => setProducts(r.data));
  };
  useEffect(() => { load(); const t = setInterval(load, 10000); return () => clearInterval(t); }, []);

  const filtered = useMemo(
    () => products.filter((p) =>
      p.name.toLowerCase().includes(q.toLowerCase()) ||
      (p.sku || "").toLowerCase().includes(q.toLowerCase())
    ).slice(0, 30),
    [products, q]
  );

  const openConfirm = (ext) => {
    setOpen(ext); setCart([]); setQ("");
  };

  const addToCart = (p) => {
    setCart((c) => {
      const ex = c.find((i) => i.product_id === p.id);
      if (ex) return c.map((i) => i.product_id === p.id ? { ...i, quantity: i.quantity + 1 } : i);
      return [...c, { product_id: p.id, name: p.name, unit_price: p.sale_price, quantity: 1 }];
    });
  };

  const changeQty = (pid, d) => setCart((c) => c.map((i) =>
    i.product_id === pid ? { ...i, quantity: Math.max(1, i.quantity + d) } : i
  ));

  const remove = (pid) => setCart((c) => c.filter((i) => i.product_id !== pid));

  const cartTotal = cart.reduce((s, i) => s + i.unit_price * i.quantity, 0);

  const confirmSale = async () => {
    if (cart.length === 0) { toast.error("Selecione os produtos vendidos"); return; }
    try {
      await api.post("/infinitepay/confirm-external", {
        pending_id: open.transaction_id, items: cart,
      });
      toast.success("Venda confirmada e estoque baixado!");
      setOpen(null); setCart([]); load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao confirmar");
    }
  };

  const openManual = () => {
    setManualOpen(true); setCart([]); setAmountReceived(0); setManualNotes(""); setQ("");
  };

  const saveManual = async () => {
    if (cart.length === 0) { toast.error("Adicione produtos"); return; }
    const received = Number(amountReceived) || cart.reduce((s, i) => s + i.unit_price * i.quantity, 0);
    try {
      await api.post("/infinitepay/manual-sale", {
        items: cart,
        amount_received: received,
        notes: manualNotes,
      });
      toast.success("Venda registrada e estoque baixado!");
      setManualOpen(false); setCart([]); setAmountReceived(0); setManualNotes(""); load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao registrar");
    }
  };

  return (
    <div className="space-y-6" data-testid="infinitepay-page">
      <header className="flex justify-between items-start flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-[#2563EB] font-semibold mb-1">Integração</div>
          <h1 className="font-display text-4xl font-bold">InfinitePay</h1>
          <p className="text-slate-500 text-sm mt-1">
            Vendas pelo PDV com QR Code são confirmadas automaticamente. Para vendas feitas direto na maquininha, clique em "Registrar Venda da Maquininha" abaixo.
          </p>
        </div>
        <button onClick={openManual} data-testid="ip-manual-btn"
          className="bg-emerald-500 hover:bg-emerald-600 text-white px-4 py-3 rounded-sm font-semibold text-sm flex items-center gap-2 shadow-sm">
          <Zap size={16} /> Registrar Venda da Maquininha
        </button>
      </header>

      {/* KPIs */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-white border border-slate-200 p-5 rounded-sm">
          <div className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-2 flex items-center gap-1"><AlertTriangle size={14} className="text-amber-500"/> Aguardando Confirmação</div>
          <div className="font-display text-3xl font-bold text-amber-600">{external.length}</div>
        </div>
        <div className="bg-white border border-slate-200 p-5 rounded-sm">
          <div className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-2 flex items-center gap-1"><CreditCard size={14} className="text-blue-500"/> PDV Aguardando Pagto</div>
          <div className="font-display text-3xl font-bold text-blue-600">{pdvPending.length}</div>
        </div>
        <div className="bg-[#0A1628] text-white p-5 rounded-sm">
          <div className="text-xs uppercase tracking-[0.2em] text-blue-300 mb-2 flex items-center gap-1"><CheckCircle size={14}/> Vendas Confirmadas</div>
          <div className="font-display text-3xl font-bold">{sales.length}</div>
        </div>
      </div>

      {/* External machine sales pending manual confirmation */}
      <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
        <div className="px-5 py-3 border-b border-slate-200 bg-amber-50">
          <h3 className="font-display font-bold text-base text-amber-800 flex items-center gap-2">
            <AlertTriangle size={16} /> Vendas na Maquininha — Aguardando Baixa ({external.length})
          </h3>
          <p className="text-xs text-amber-700 mt-1">Vendas feitas direto na maquininha InfinitePay. Clique em "Dar baixa" para selecionar os produtos que foram vendidos.</p>
        </div>
        {external.length === 0 ? (
          <div className="p-8 text-center text-sm text-slate-500">Nenhuma venda externa aguardando. Tudo em dia ✓</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-600">
              <tr>
                <th className="text-left p-3">Data</th>
                <th className="text-left p-3">Transação</th>
                <th className="text-right p-3">Valor</th>
                <th className="text-right p-3"></th>
              </tr>
            </thead>
            <tbody>
              {external.map((e) => (
                <tr key={e.transaction_id} className="border-t border-slate-100" data-testid={`ip-ext-${e.transaction_id}`}>
                  <td className="p-3 text-slate-600">{(e.created_at || "").slice(0, 16).replace("T", " ")}</td>
                  <td className="p-3 font-mono text-xs">{String(e.transaction_id).slice(0, 20)}</td>
                  <td className="p-3 text-right font-semibold text-emerald-600">{brl(e.amount)}</td>
                  <td className="p-3 text-right">
                    <button onClick={() => openConfirm(e)} data-testid={`ip-confirm-${e.transaction_id}`}
                      className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white text-xs font-semibold px-3 py-1.5 rounded-sm">
                      Dar baixa
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Recent paid sales */}
      <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
        <div className="px-5 py-3 border-b border-slate-200">
          <h3 className="font-display font-bold text-base">Últimas Vendas InfinitePay</h3>
        </div>
        {sales.length === 0 ? (
          <div className="p-8 text-center text-sm text-slate-500">Nenhuma venda InfinitePay ainda.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-600">
              <tr>
                <th className="text-left p-3">Data</th>
                <th className="text-left p-3">Cliente</th>
                <th className="text-left p-3">Tipo</th>
                <th className="text-right p-3">Total</th>
              </tr>
            </thead>
            <tbody>
              {sales.slice(0, 20).map((s) => (
                <tr key={s.id} className="border-t border-slate-100">
                  <td className="p-3 text-slate-600">{(s.created_at || "").slice(0, 16).replace("T", " ")}</td>
                  <td className="p-3">{s.customer_name || "—"}</td>
                  <td className="p-3">
                    <span className={`text-xs px-2 py-0.5 rounded-sm ${s.is_external ? "bg-amber-100 text-amber-700" : "bg-emerald-100 text-emerald-700"}`}>
                      {s.is_external ? "Maquininha" : "PDV"}
                    </span>
                  </td>
                  <td className="p-3 text-right font-semibold text-emerald-600">{brl(s.total)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Confirm modal */}
      {open && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-sm max-w-3xl w-full max-h-[90vh] flex flex-col" data-testid="ip-confirm-modal">
            <div className="flex justify-between items-center p-5 border-b border-slate-200">
              <div>
                <h2 className="font-display text-xl font-bold">Dar baixa — Venda Maquininha</h2>
                <p className="text-sm text-slate-500">Valor recebido: <strong>{brl(open.amount)}</strong> • Transação: {String(open.transaction_id).slice(0, 16)}</p>
              </div>
              <button onClick={() => setOpen(null)}><X size={20} /></button>
            </div>

            <div className="grid grid-cols-2 flex-1 overflow-hidden">
              {/* Product search */}
              <div className="border-r border-slate-200 p-4 overflow-auto">
                <div className="relative mb-3">
                  <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input type="text" placeholder="Buscar produto..." value={q} onChange={(e) => setQ(e.target.value)}
                    data-testid="ip-confirm-search"
                    className="w-full pl-9 pr-3 py-2 border border-slate-300 rounded-sm text-sm" />
                </div>
                <div className="space-y-1">
                  {filtered.map((p) => (
                    <button key={p.id} onClick={() => addToCart(p)}
                      className="w-full text-left border border-slate-200 hover:border-[#2563EB] p-2 rounded-sm text-sm flex justify-between items-center">
                      <div className="flex-1 min-w-0">
                        <div className="font-medium truncate">{p.name}</div>
                        <div className="text-xs text-slate-500">{p.sku || "—"} • Estoque: {p.stock}</div>
                      </div>
                      <div className="text-[#2563EB] font-semibold text-sm ml-2">{brl(p.sale_price)}</div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Selected items */}
              <div className="p-4 overflow-auto flex flex-col">
                <div className="font-semibold text-sm mb-3 text-slate-700">Produtos vendidos ({cart.length})</div>
                {cart.length === 0 ? (
                  <div className="text-sm text-slate-500 py-8 text-center">Clique nos produtos à esquerda para adicionar</div>
                ) : (
                  <ul className="space-y-2 flex-1 overflow-auto">
                    {cart.map((i) => (
                      <li key={i.product_id} className="border border-slate-200 p-2 rounded-sm">
                        <div className="flex justify-between text-sm font-medium">
                          <span className="line-clamp-1">{i.name}</span>
                          <button onClick={() => remove(i.product_id)} className="text-red-500"><Trash2 size={13} /></button>
                        </div>
                        <div className="flex justify-between items-center mt-1">
                          <div className="flex items-center gap-1">
                            <button onClick={() => changeQty(i.product_id, -1)} className="w-6 h-6 border border-slate-300 rounded-sm"><Minus size={12} /></button>
                            <span className="px-2 text-sm">{i.quantity}</span>
                            <button onClick={() => changeQty(i.product_id, 1)} className="w-6 h-6 border border-slate-300 rounded-sm"><Plus size={12} /></button>
                          </div>
                          <span className="font-semibold text-sm">{brl(i.unit_price * i.quantity)}</span>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
                <div className="mt-3 pt-3 border-t border-slate-200 space-y-2 text-sm">
                  <div className="flex justify-between text-slate-600"><span>Total produtos</span><span>{brl(cartTotal)}</span></div>
                  <div className="flex justify-between font-semibold"><span>Valor recebido</span><span className="text-emerald-600">{brl(open.amount)}</span></div>
                  {Math.abs(cartTotal - open.amount) > 0.01 && (
                    <div className="text-xs text-amber-600">⚠ Valor dos produtos ≠ valor recebido. Pode confirmar mesmo assim.</div>
                  )}
                  <button onClick={confirmSale} disabled={cart.length === 0}
                    data-testid="ip-save-confirm"
                    className="w-full mt-2 bg-[#2563EB] hover:bg-[#1D4ED8] disabled:opacity-50 text-white font-semibold py-2.5 rounded-sm">
                    Confirmar venda e dar baixa no estoque
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Quick manual register modal */}
      {manualOpen && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-sm max-w-3xl w-full max-h-[90vh] flex flex-col" data-testid="ip-manual-modal">
            <div className="flex justify-between items-center p-5 border-b border-slate-200 bg-emerald-50">
              <div>
                <div className="text-xs uppercase tracking-[0.25em] text-emerald-700 font-semibold">Registro Rápido</div>
                <h2 className="font-display text-xl font-bold">Venda da Maquininha InfinitePay</h2>
                <p className="text-sm text-slate-600 mt-0.5">Passou o cartão na maquininha? Adicione aqui os produtos e dê baixa no estoque em 10 segundos.</p>
              </div>
              <button onClick={() => setManualOpen(false)} data-testid="ip-manual-close"><X size={20} /></button>
            </div>

            <div className="grid grid-cols-2 flex-1 overflow-hidden">
              <div className="border-r border-slate-200 p-4 overflow-auto">
                <div className="relative mb-3">
                  <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input type="text" placeholder="Buscar produto..." value={q} onChange={(e) => setQ(e.target.value)}
                    data-testid="ip-manual-search"
                    className="w-full pl-9 pr-3 py-2 border border-slate-300 rounded-sm text-sm" />
                </div>
                <div className="space-y-1">
                  {filtered.map((p) => (
                    <button key={p.id} onClick={() => addToCart(p)}
                      data-testid={`ip-manual-add-${p.id}`}
                      className="w-full text-left border border-slate-200 hover:border-[#2563EB] p-2 rounded-sm text-sm flex justify-between items-center">
                      <div className="flex-1 min-w-0">
                        <div className="font-medium truncate">{p.name}</div>
                        <div className="text-xs text-slate-500">{p.sku || "—"} • Estoque: {p.stock}</div>
                      </div>
                      <div className="text-[#2563EB] font-semibold text-sm ml-2">{brl(p.sale_price)}</div>
                    </button>
                  ))}
                </div>
              </div>

              <div className="p-4 overflow-auto flex flex-col">
                <div className="font-semibold text-sm mb-3 text-slate-700">Produtos vendidos ({cart.length})</div>
                {cart.length === 0 ? (
                  <div className="text-sm text-slate-500 py-8 text-center">Clique nos produtos à esquerda para adicionar</div>
                ) : (
                  <ul className="space-y-2 flex-1 overflow-auto">
                    {cart.map((i) => (
                      <li key={i.product_id} className="border border-slate-200 p-2 rounded-sm">
                        <div className="flex justify-between text-sm font-medium">
                          <span className="line-clamp-1">{i.name}</span>
                          <button onClick={() => remove(i.product_id)} className="text-red-500"><Trash2 size={13} /></button>
                        </div>
                        <div className="flex justify-between items-center mt-1">
                          <div className="flex items-center gap-1">
                            <button onClick={() => changeQty(i.product_id, -1)} className="w-6 h-6 border border-slate-300 rounded-sm"><Minus size={12} /></button>
                            <span className="px-2 text-sm">{i.quantity}</span>
                            <button onClick={() => changeQty(i.product_id, 1)} className="w-6 h-6 border border-slate-300 rounded-sm"><Plus size={12} /></button>
                          </div>
                          <span className="font-semibold text-sm">{brl(i.unit_price * i.quantity)}</span>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
                <div className="mt-3 pt-3 border-t border-slate-200 space-y-3">
                  <div className="flex justify-between text-sm text-slate-600">
                    <span>Soma dos produtos</span>
                    <span className="font-semibold">{brl(cartTotal)}</span>
                  </div>
                  <div>
                    <label className="block text-xs uppercase tracking-wider text-slate-600 mb-1">Valor recebido na maquininha</label>
                    <input type="number" step="0.01" value={amountReceived}
                      onChange={(e) => setAmountReceived(e.target.value)}
                      placeholder={cartTotal.toFixed(2)}
                      data-testid="ip-manual-amount"
                      className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm" />
                    <div className="text-xs text-slate-400 mt-1">Deixe em branco para usar o total dos produtos</div>
                  </div>
                  <div>
                    <label className="block text-xs uppercase tracking-wider text-slate-600 mb-1">Observações</label>
                    <input type="text" value={manualNotes} onChange={(e) => setManualNotes(e.target.value)}
                      placeholder="Ex: cliente João, crédito 2x"
                      data-testid="ip-manual-notes"
                      className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm" />
                  </div>
                  <button onClick={saveManual} disabled={cart.length === 0}
                    data-testid="ip-manual-save"
                    className="w-full bg-emerald-500 hover:bg-emerald-600 disabled:opacity-50 text-white font-semibold py-2.5 rounded-sm flex items-center justify-center gap-2">
                    <Zap size={14} /> Registrar venda e dar baixa
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
